import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QUESTION_BANK, QuestionBankItem, getQuestionsByCategory, searchQuestions } from '../../../utils/defaultFormsSetup';

interface QuestionBankSidebarProps {
  onQuestionDrop: (question: QuestionBankItem, index?: number) => void;
}

export function QuestionBankSidebar({ onQuestionDrop }: QuestionBankSidebarProps) {
  const { t, i18n } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [draggedQuestion, setDraggedQuestion] = useState<QuestionBankItem | null>(null);

  const getFilteredQuestions = () => {
    let questions = QUESTION_BANK;

    if (selectedCategory !== 'all') {
      questions = getQuestionsByCategory(selectedCategory);
    }

    if (searchTerm.trim()) {
      questions = searchQuestions(searchTerm);
    }

    return questions;
  };

  const handleDragStart = (e: React.DragEvent, question: QuestionBankItem) => {
    setDraggedQuestion(question);
    e.dataTransfer.setData('text/plain', JSON.stringify(question));
    e.dataTransfer.effectAllowed = 'copy';
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '0.5';
    }
  };

  const handleDragEnd = (e: React.DragEvent) => {
    setDraggedQuestion(null);
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '1';
    }
  };

  const filteredQuestions = getFilteredQuestions();

  return (
    <div style={{
      width: '350px',
      background: '#f8f9fa',
      borderRight: '1px solid #e9ecef',
      height: 'calc(100vh - 40px)',
      position: 'sticky',
      top: '20px',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        padding: '20px',
        borderBottom: '1px solid #e9ecef',
        background: 'white'
      }}>
        <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>
          📚 {t('questionBank.title') || 'Question Bank'}
        </h3>
        
        {/* Search */}
        <input
          type="text"
          placeholder={t('questionBank.searchPlaceholder') || 'Search questions...'}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: '100%',
            padding: '10px 12px',
            border: '1px solid #ced4da',
            borderRadius: '6px',
            fontSize: '14px',
            marginBottom: '12px'
          }}
        />
        
        {/* Category Filter */}
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          style={{
            width: '100%',
            padding: '10px 12px',
            border: '1px solid #ced4da',
            borderRadius: '6px',
            fontSize: '14px'
          }}
        >
          <option value="all">{t('questionBank.allCategories') || 'All Categories'}</option>
          <option value="rights">{t('questionBank.rights') || 'Rights'}</option>
          <option value="emotional">{t('questionBank.emotional') || 'Emotional'}</option>
          <option value="professional">{t('questionBank.professional') || 'Professional'}</option>
          <option value="general">{t('questionBank.general') || 'General'}</option>
        </select>
      </div>

      {/* Questions List */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '16px'
      }}>
        {filteredQuestions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: '#666' }}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>🔍</div>
            <p style={{ fontSize: '14px' }}>{t('questionBank.noQuestions') || 'No questions found'}</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {filteredQuestions.map((question) => (
              <div
                key={question.id}
                draggable
                onDragStart={(e) => handleDragStart(e, question)}
                onDragEnd={handleDragEnd}
                style={{
                  padding: '12px',
                  border: '1px solid #e9ecef',
                  borderRadius: '8px',
                  cursor: 'grab',
                  background: 'white',
                  transition: 'all 0.2s ease',
                  userSelect: 'none'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#007acc';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,122,204,0.15)';
                  e.currentTarget.style.cursor = 'grab';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e9ecef';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '6px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '4px' }}>
                      {question.labelHe}
                    </div>
                    <div style={{ fontSize: '11px', color: '#666' }}>
                      {question.field_name}
                    </div>
                  </div>
                  <div style={{
                    background: getCategoryColor(question.category),
                    color: 'white',
                    padding: '2px 8px',
                    borderRadius: '10px',
                    fontSize: '10px',
                    fontWeight: 'bold',
                    whiteSpace: 'nowrap',
                    marginLeft: '8px'
                  }}>
                    {getCategoryLabel(question.category, t)}
                  </div>
                </div>
                
                <div style={{ fontSize: '12px', color: '#999', marginTop: '6px' }}>
                  {getTypeLabel(question.type, t)}
                  {question.options && question.options.length > 0 && (
                    <span> • {question.options.length} {t('formBuilder.options')}</span>
                  )}
                </div>
                
                <div style={{ 
                  fontSize: '11px', 
                  color: '#999', 
                  marginTop: '8px',
                  fontStyle: 'italic'
                }}>
                  👆 Drag to add
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{
        padding: '16px',
        borderTop: '1px solid #e9ecef',
        background: 'white',
        fontSize: '12px',
        color: '#666',
        textAlign: 'center'
      }}>
        {filteredQuestions.length} {t('questionBank.questionsFound') || 'questions'}
      </div>
    </div>
  );
}

function getCategoryColor(category: string): string {
  switch (category) {
    case 'rights': return '#2ecc71';
    case 'emotional': return '#e74c3c';
    case 'professional': return '#3498db';
    case 'general': return '#95a5a6';
    default: return '#6c757d';
  }
}

function getCategoryLabel(category: string, t: any): string {
  switch (category) {
    case 'rights': return t('questionBank.rights') || 'Rights';
    case 'emotional': return t('questionBank.emotional') || 'Emotional';
    case 'professional': return t('questionBank.professional') || 'Professional';
    case 'general': return t('questionBank.general') || 'General';
    default: return category;
  }
}

function getTypeLabel(type: string, t: any): string {
  const typeKey = `formBuilder.fieldTypes.${type}`;
  return t(typeKey) || type;
}

export default QuestionBankSidebar;

